/**
 * Shared presentation helpers for the public job-discovery UI.
 * Pure formatting only — no data fetching, no side effects.
 */

/** Human label for a job type value e.g. "full-time" -> "Full-time". */
export const formatJobType = (type) => {
  if (!type) return '';
  return String(type)
    .split('-')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join('-');
};

/** Human label for an experience level e.g. "mid" -> "Mid level". */
export const formatExperience = (level) => {
  if (!level) return '';
  const map = {
    entry: 'Entry level',
    intermediate: 'Intermediate level',
    mid: 'Mid level',
    senior: 'Senior level',
    expert: 'Expert level',
    lead: 'Lead level',
    executive: 'Executive level',
  };
  const key = String(level).toLowerCase();
  return map[key] || formatJobType(level);
};

/** Human label for a work mode e.g. "onsite" -> "On-site". */
export const formatWorkMode = (mode) => {
  if (!mode) return '';
  const map = {
    onsite: 'On-site',
    'on-site': 'On-site',
    remote: 'Remote',
    hybrid: 'Hybrid',
    flexible: 'Flexible',
  };
  const key = String(mode).toLowerCase();
  return map[key] || formatJobType(mode);
};

/** Salary range text, or '' when no salary data exists. */
export const formatSalary = (min, max) => {
  const n = (v) => (typeof v === 'number' && v > 0 ? v : 0);
  const lo = n(min);
  const hi = n(max);

  const short = (value) =>
    value >= 10000000
      ? `₹${(value / 10000000).toFixed(value % 10000000 === 0 ? 0 : 1)}Cr`
      : value >= 100000
        ? `₹${(value / 100000).toFixed(value % 100000 === 0 ? 0 : 1)}L`
        : `₹${(value / 1000).toFixed(0)}K`;

  if (lo && hi) return `${short(lo)} – ${short(hi)}`;
  if (lo) return `From ${short(lo)}`;
  if (hi) return `Up to ${short(hi)}`;
  return '';
};

/** Relative posted date: Today / Yesterday / N days ago / N weeks ago / date. */
export const timeAgo = (input) => {
  if (!input) return '';
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return '';

  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

/** Absolute date e.g. "15 March 2026". */
export const formatDate = (input) => {
  if (!input) return '';
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

/** Up to two uppercase initials from a name. */
export const initials = (name) => {
  if (!name) return '?';
  return String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join('')
    .toUpperCase();
};

/** Split a comma/newline separated skills string into a clean array. */
export const splitSkills = (value) => {
  if (!value) return [];
  return String(value)
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
};

/** Build a slug suitable for a location route from a free-text location. */
export const locationSlug = (location) =>
  String(location || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/** Turn a location slug back into a display label ("new-delhi" -> "New Delhi"). */
export const slugToLabel = (slug) =>
  String(slug || '')
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
